import { useLocation } from 'react-router-dom';

import { useCurrentWorldContext } from '../../context/currentWorld';
import { useCurrentUserContext } from '../../context/currentUser';
import { useFeatureTogglesContext } from '../../context/featureToggles';
import { useWorldRoutes, AllWorldRoutes } from '../../routes/parts/allWorldRoutes';
import { getCoreAccessFlags } from '../../containers/app/productsWrapper';
import { ReactComponent as GridFourIcon } from '@components/icons/gridFour.svg';
import { ReactComponent as ArrowsToCircleIcon } from '@components/icons/arrowsToCircle.svg';
import { ISelectorBarTab } from './selectorBar';

export interface SidebarTabsResult {
  tabs: ISelectorBarTab[];
  initialSelection: string;
  enabled: boolean;
}

const coreTitles = {
  devices: 'DEVICES',
  performance: 'PERFORMANCE',
  applications: 'APPLICATIONS',
  processes: 'PROCESSES',
  anomalies: 'ANOMALIES',
  maps: 'MOBILE',
  accessPoints: 'ACCESS_POINTS',
  anomalyDetectionBeta: 'ANOMALIES_BETA',
  batteries: 'BATTERY_FIRST_CAPITAL_other',
} as const;

interface CoreTabOptions {
  hasAndroidDevices: boolean;
  hasWindowsDevices: boolean;
  isAdministrator: boolean;
  isManager: boolean;
  showAccessPointsPage: boolean;
  showAnomalyDetectionBeta: boolean;
  hasCoreAccess: boolean;
  showBatteriesHub: boolean;
}

// Two independent dimensions per entry: whether it appears at all (unchanged - capability/role/
// toggle, exactly as before), and whether it is disabled (new - the world lacks the underlying
// product licence for that entry). hasCoreAccess/showBatteriesHub come from the same shared
// helper used by core-route.tsx's <Access> gate, so a tab's disabled state never drifts from
// whether navigating there actually works.
export const getCoreTabs = (routes: AllWorldRoutes, options: CoreTabOptions): ISelectorBarTab[] => {
  const { hasAndroidDevices, hasWindowsDevices, isAdministrator, isManager, showAccessPointsPage, showAnomalyDetectionBeta, hasCoreAccess, showBatteriesHub } = options;
  const disabled = !hasCoreAccess;
  const tabs: ISelectorBarTab[] = [
    { id: 'devicesSelectorBarBtn', link: routes.core.devices, title: coreTitles.devices, icon: 'fas fa-mobile-alt', disabled },
    { id: 'batteriesSelectorBarBtn', link: routes.core.batteries, title: coreTitles.batteries, icon: 'fas fa-battery-three-quarters', disabled: !showBatteriesHub },
    { id: 'performanceSelectorBarBtn', link: routes.core.performance, title: coreTitles.performance, icon: 'fas fa-chart-line', disabled },
  ];
  if (hasAndroidDevices) tabs.push({ id: 'applicationsSelectorBarBtn', link: routes.core.applicationsList, title: coreTitles.applications, icon: GridFourIcon, disabled });
  if (hasWindowsDevices) tabs.push({ id: 'processesSelectorBarBtn', link: routes.core.processesList, title: coreTitles.processes, icon: 'fas fa-list-ul', disabled });
  if (isAdministrator || isManager) tabs.push({ id: 'anomaliesSelectorBarBtn', link: routes.core.anomalies, title: coreTitles.anomalies, icon: ArrowsToCircleIcon, disabled });
  tabs.push({ id: 'mapsSelectorBarBtn', link: routes.core.maps, title: coreTitles.maps, icon: 'fas fa-tower-cell', disabled });
  if (showAccessPointsPage && hasAndroidDevices) tabs.push({ id: 'accessPointsSelectorBarBtn', link: routes.core.accessPoints, title: coreTitles.accessPoints, icon: 'fas fa-wifi', disabled });
  if (showAnomalyDetectionBeta) tabs.push({ id: 'anomalyDetectionBetaSelectorBarBtn', link: routes.core.anomalyDetectionBeta, title: coreTitles.anomalyDetectionBeta, icon: ArrowsToCircleIcon, disabled });
  return tabs;
};

export const getCoreInitialSelection = (path: string): string => {
  const subPath = path.split('/core')[1] || '';
  if (subPath.startsWith('/devices')) return coreTitles.devices;
  if (subPath.startsWith('/performance')) return coreTitles.performance;
  if (subPath.startsWith('/applications')) return coreTitles.applications;
  if (subPath.startsWith('/processes')) return coreTitles.processes;
  if (subPath.startsWith('/anomalies-beta')) return coreTitles.anomalyDetectionBeta;
  if (subPath.startsWith('/anomalies')) return coreTitles.anomalies;
  if (subPath.startsWith('/maps')) return coreTitles.maps;
  if (subPath.startsWith('/accessPoints')) return coreTitles.accessPoints;
  if (subPath.startsWith('/batteries')) return coreTitles.batteries;
  return coreTitles.devices;
};

// The Core shell is now the only sidebar experience - the standalone /battery rail is gone,
// its routes redirect into /core/batteries, so there is nothing left to detect a '/battery'
// pathname for.
export const useSidebarTabs = (): SidebarTabsResult => {
  const { pathname } = useLocation();
  const routes = useWorldRoutes();
  const { hasAndroidDevices, hasWindowsDevices, features } = useCurrentWorldContext();
  const { isAdministrator, isManager } = useCurrentUserContext();
  const { showAccessPointsPage, showAnomalyDetectionBeta } = useFeatureTogglesContext();

  if (pathname.includes('/core')) {
    const { hasCoreAccess, showBatteriesHub } = getCoreAccessFlags(features);
    return {
      tabs: getCoreTabs(routes, { hasAndroidDevices, hasWindowsDevices, isAdministrator, isManager, showAccessPointsPage, showAnomalyDetectionBeta, hasCoreAccess, showBatteriesHub }),
      initialSelection: getCoreInitialSelection(pathname),
      enabled: true,
    };
  }

  return { tabs: [], initialSelection: '', enabled: false };
};
